import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { DeviceMobile, Download } from '@phosphor-icons/react';

/**
 * PWA install button + service worker registration for the admin back-office.
 * - Registers /sw.js so the app is installable
 * - Listens for the `beforeinstallprompt` event and exposes a button when the
 *   install prompt is available (Chrome, Edge, Android)
 * - Falls back to an iOS install instructions dialog when applicable
 */
export const PwaInstallButton = ({ compact = false }) => {
  const deferredPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Register service worker (required for install prompt)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    // Prompt may have fired before this component mounted (captured globally in index.js)
    if (window.__pwaPrompt) {
      deferredPromptRef.current = window.__pwaPrompt;
      setCanInstall(true);
    }

    const onBip = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };
    const onReady = () => {
      deferredPromptRef.current = window.__pwaPrompt;
      setCanInstall(true);
    };
    const onInstalled = () => {
      setCanInstall(false);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('pwa-prompt-ready', onReady);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('pwa-prompt-ready', onReady);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current || window.__pwaPrompt;
    const ua = window.navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (prompt) {
      prompt.prompt();
      await prompt.userChoice;
      deferredPromptRef.current = null;
      window.__pwaPrompt = null;
      setCanInstall(false);
    } else if (isIos) {
      setShowIosHint(true);
    }
  };

  if (installed) return null;

  const ua = typeof window !== 'undefined' ? window.navigator.userAgent || '' : '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!canInstall && !isIos) return null;

  return (
    <>
      <Button
        variant="outline"
        size={compact ? 'sm' : 'default'}
        onClick={handleInstall}
        data-testid="pwa-install-btn"
        className="gap-2 border-[#c2185b]/40 text-[#c2185b] hover:bg-[#c2185b]/5"
      >
        {compact ? <Download size={16} weight="bold" /> : <DeviceMobile size={16} weight="fill" />}
        {compact ? <span className="hidden sm:inline">Installer</span> : "Installer l'app"}
      </Button>

      {showIosHint && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 p-3"
          onClick={() => setShowIosHint(false)}
          data-testid="pwa-ios-hint"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <DeviceMobile size={20} weight="fill" className="text-[#c2185b]" />
              Installer sur iPhone / iPad
            </h3>
            <ol className="text-sm space-y-2 text-slate-700 list-decimal ml-5">
              <li>Ouvrez ce site dans <b>Safari</b> (pas Chrome).</li>
              <li>Touchez l'icône <b>Partager</b> (carré avec flèche vers le haut).</li>
              <li>Faites défiler et choisissez <b>« Sur l'écran d'accueil »</b>.</li>
              <li>Validez avec <b>Ajouter</b>.</li>
            </ol>
            <p className="text-xs text-slate-400 mt-3">
              Une icône MAGERE apparaîtra sur votre écran d'accueil. L'app s'ouvrira en plein écran comme une vraie appli.
            </p>
            <Button className="w-full mt-4 bg-[#c2185b] hover:bg-[#a11550]" onClick={() => setShowIosHint(false)}>
              Compris
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaInstallButton;
